import { API_ENDPOINTS, OLYMP_API_BASE_URL } from '../config/api';

const COHORTS_BASE = `${OLYMP_API_BASE_URL}${API_ENDPOINTS.COHORTS.LIST}`.replace(/\/$/, '');
const FETCH_TIMEOUT_MS = 10000;

function buildCohortUrl(path = '') {
  const normalizedPath = String(path).replace(/^\/+|\/+$/g, '');
  if (!normalizedPath) {
    return `${COHORTS_BASE}/`;
  }
  return `${COHORTS_BASE}/${normalizedPath}/`.replace(/([^:]\/)\/+/g, '$1');
}

function buildHeaders() {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function cohortFetch(url, options = {}) {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      method: options.method || 'GET',
      headers: buildHeaders(),
      body: options.body ? JSON.stringify(options.body) : undefined,
      signal: controller.signal,
    });

    if (!response.ok) {
      const error = new Error(
        response.status === 401
          ? 'Authentication required. Please sign in again.'
          : `Cohort API error ${response.status}`
      );
      error.status = response.status;
      error.url = url;
      throw error;
    }

    if (response.status === 204) {
      return null;
    }

    return response.json();
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function normalizeArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeCohortSummary(item) {
  if (!item || typeof item !== 'object') {
    return null;
  }

  return {
    id: item.id,
    name: item.name || '',
    description: item.description || '',
    status: item.status || 'unedited',
    parentId: item.parentId ?? null,
    childCount: Number(item.childCount || 0),
    parameterListId: item.parameterListId || null,
    parameterListName: item.parameterListName || '',
    isPublic: Boolean(item.isPublic),
    patientCount: item.patientCount ?? null,
    usageCount: Number(item.usageCount || 0),
    createdAt: item.createdAt || null,
    updatedAt: item.updatedAt || null,
    children: normalizeArray(item.children).map(normalizeCohortSummary).filter(Boolean),
  };
}

function normalizeCohortDetail(item) {
  if (!item || typeof item !== 'object') {
    return null;
  }

  return {
    ...normalizeCohortSummary(item),
    createdBy: item.createdBy || '',
    owner: item.owner || '',
    graph: item.graph || null,
  };
}

export async function listCohorts() {
  const payload = await cohortFetch(buildCohortUrl());
  const results = normalizeArray(payload?.results ?? payload).map(normalizeCohortSummary).filter(Boolean);
  return {
    count: Number(payload?.count ?? results.length),
    results,
  };
}

export async function getCohort(id) {
  const payload = await cohortFetch(buildCohortUrl(id));
  return normalizeCohortDetail(payload);
}

export async function createCohort(body = {}) {
  const payload = await cohortFetch(buildCohortUrl(), {
    method: 'POST',
    body,
  });
  return normalizeCohortDetail(payload);
}

export async function saveCohortGraph(id, graph) {
  const payload = await cohortFetch(buildCohortUrl(`${id}/graph`), {
    method: 'POST',
    body: { graph },
  });
  return normalizeCohortDetail(payload);
}

export async function duplicateCohort(id) {
  const payload = await cohortFetch(buildCohortUrl(`${id}/duplicate`), {
    method: 'POST',
  });
  return normalizeCohortDetail(payload);
}

export async function deleteCohort(id) {
  await cohortFetch(buildCohortUrl(id), {
    method: 'DELETE',
  });
}
